import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import {
  useDockLayoutStore,
  selectBottomDockOffset,
} from '@/stores/useDockLayoutStore';

const GAP = 12;

type ClearanceRect = { top: number; bottom: number; left: number; right: number };

interface Options {
  /**
   * Publish this element's EFFECTIVE (post-lift) rect to a store setter so a
   * control stacked above it can clear it in turn. The bottom-right stack is
   * Save bar → SynthBackgroundPill → MaieLauncherPill: the synth card reports
   * its lifted rect via `setSynthCard`, and the launcher clears that.
   */
  report?: (rect: ClearanceRect | null) => void;
  /**
   * Whether to clear the synth card. Default true (launcher / feedback widget).
   * The synth card passes false so it never tries to climb above its OWN
   * reported rect.
   */
  includeSynthCard?: boolean;
}

/**
 * Compute how far a `fixed bottom-*` floating control must lift to clear the
 * bottom obstacles — returns `{ ref, lift }`. Attach `ref` to the element and
 * apply `translateY(-lift)` with NO transform transition (a transition makes
 * the measured anchor wrong).
 *
 * Obstacles, all in true viewport coords (the app renders inside a transformed
 * ancestor, so `fixed` is relative to a sub-region — getBoundingClientRect
 * normalizes that):
 *  - Fixed full-width bottom docks (recording / next-appointment / pending-sync)
 *    report a HEIGHT → lift = height + gap, applied to every floating control.
 *  - Rect obstacles (the sticky "Save & sign" action bar, and the bottom-right
 *    SynthBackgroundPill) report their live rect. We lift above one ONLY while
 *    it overlaps this element's resting corner on BOTH axes — vertical AND
 *    horizontal — so a wide-screen control off in the gutter beside a centred
 *    bar doesn't lift, and the bottom-LEFT feedback widget never lifts for the
 *    bottom-RIGHT synth card. Once an obstacle rises clear of the corner, its
 *    lift is 0 (the control sits in its corner UNDER it).
 *
 * Stable callback ref + ResizeObserver so the element's own size changes (the
 * synth card expanding its popover) recompute the lift, and so conditionally-
 * rendered controls (re)attach correctly.
 */
export function useFloatingClearance<T extends HTMLElement = HTMLElement>(
  opts: Options = {},
) {
  const fixedDockOffset = useDockLayoutStore(selectBottomDockOffset);
  const actionBar = useDockLayoutStore((s) => s.actionBar);
  const synthCard = useDockLayoutStore((s) => s.synthCard);

  const elRef = useRef<T | null>(null);
  const liftRef = useRef(0);
  const [lift, setLift] = useState(0);

  // Latest inputs mirrored into a ref so `recompute` (and thus the callback
  // ref) can stay STABLE — a ref callback whose identity changed every store
  // tick would detach/reattach the element and thrash the ResizeObserver.
  const inputs = useRef({ actionBar, synthCard, fixedDockOffset, opts });
  inputs.current = { actionBar, synthCard, fixedDockOffset, opts };

  const recompute = useCallback(() => {
    const el = elRef.current;
    if (!el) return;
    const {
      actionBar,
      synthCard,
      fixedDockOffset,
      opts: { report, includeSynthCard = true },
    } = inputs.current;

    const rect = el.getBoundingClientRect();
    // Anchor edges are invariant under our own translateY: undo the applied
    // lift to recover the resting corner rect (left/right unaffected).
    const naturalBottom = rect.bottom + liftRef.current;
    const naturalTop = naturalBottom - rect.height;

    const rectLift = (o: ClearanceRect | null): number => {
      if (
        o == null ||
        o.top >= naturalBottom ||
        o.bottom <= naturalTop ||
        o.left >= rect.right ||
        o.right <= rect.left
      ) {
        return 0;
      }
      return Math.max(0, naturalBottom - o.top + GAP);
    };

    const dockLift = fixedDockOffset > 0 ? fixedDockOffset + GAP : 0;
    const next = Math.max(
      rectLift(actionBar),
      includeSynthCard ? rectLift(synthCard) : 0,
      dockLift,
    );

    if (Math.abs(next - liftRef.current) > 0.5) {
      liftRef.current = next;
      setLift(next);
    }
    // Publish the effective post-lift rect (computed, not re-measured, so no
    // measurement race against the transform we just applied).
    report?.({
      top: naturalTop - next,
      bottom: naturalBottom - next,
      left: rect.left,
      right: rect.right,
    });
  }, []);

  // Recompute whenever an obstacle moves.
  useLayoutEffect(() => {
    recompute();
  }, [actionBar, synthCard, fixedDockOffset, recompute]);

  const roRef = useRef<ResizeObserver | null>(null);
  const ref = useCallback(
    (el: T | null) => {
      roRef.current?.disconnect();
      roRef.current = null;
      elRef.current = el;
      if (!el) {
        // Detached (control unmounted) → clear any reported rect.
        inputs.current.opts.report?.(null);
        return;
      }
      const ro = new ResizeObserver(() => recompute());
      ro.observe(el);
      roRef.current = ro;
      recompute();
    },
    [recompute],
  );

  return { ref, lift };
}
